import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, FormSection, Button, Select, DataTable, ErrorState } from '@airclou/ui'
import type { DataTableColumn } from '@airclou/ui'
import { Download } from 'lucide-react'
import { useTimezone } from '@airclou/shell/timezone'
import { listInvoices } from '../../../services/billing'
import type { InvoiceSummary } from '../../../services/billing'
import { formatMoney } from '../components/invoiceDisplay'

/**
 * Spending report, computed in the browser from the same Stripe invoice list
 * the Invoices tab shows. Nothing is stored or estimated.
 *
 * Until 2026-10-05 this tab rendered four hardcoded "Professional Plan $29.99"
 * months and made-up usage bars for every organization. There is no usage
 * source behind billing yet, so the report covers money only.
 *
 * Void invoices are left out of the totals (Stripe cancelled them; nothing
 * was owed). "Outstanding" is what open invoices still ask for; an
 * uncollectible invoice's remainder is written off, not outstanding. Amounts
 * are summed per currency and never converted.
 */

type Range = '3' | '6' | '12' | 'all'

interface MonthRow {
  key: string
  month: string
  currency: string
  invoiceCount: number
  billedCents: number
  paidCents: number
  outstandingCents: number
}

interface Totals {
  billedCents: number
  paidCents: number
  outstandingCents: number
}

/** "YYYY-MM" of an instant in the viewer's timezone, so months match the dates shown elsewhere. */
function monthKey(iso: string, timezone: string | null | undefined): string {
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit' }
  let parts: Intl.DateTimeFormatPart[]
  try {
    parts = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: timezone ?? undefined }).formatToParts(new Date(iso))
  } catch {
    // An unknown timezone name throws; fall back to the browser's own.
    parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(new Date(iso))
  }
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}`
}

function monthLabel(key: string, locale: string): string {
  const [year, month] = key.split('-').map(Number)
  // Mid-month at noon UTC, formatted in UTC: the label can't slip a month.
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, 15, 12))
  )
}

/**
 * First "YYYY-MM" the range includes. Ranges are whole calendar months — this
 * month and the N-1 before it — so the oldest row in the breakdown is never a
 * partial month labelled as a full one.
 */
function firstMonthOf(range: Range, timezone: string | null | undefined): string | null {
  if (range === 'all') return null
  const [year, month] = monthKey(new Date().toISOString(), timezone).split('-').map(Number)
  const d = new Date(Date.UTC(year, month - 1 - (Number(range) - 1), 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

function csvCell(value: string | number | null): string {
  const s = value === null ? '' : String(value)
  return `"${s.replace(/"/g, '""')}"`
}

/** A text cell starting with = + - @ runs as a formula in a spreadsheet; a leading ' keeps it text. */
function csvText(value: string | null): string | null {
  return value && /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
}

function downloadCsv(invoices: InvoiceSummary[]) {
  const headers = ['Invoice', 'Date', 'Description', 'Status', 'Currency', 'Total', 'Paid', 'Due']
  const rows = invoices.map((inv) => [
    csvText(inv.number ?? inv.id),
    inv.createdAt,
    csvText(inv.description),
    inv.status,
    inv.currency.toUpperCase(),
    (inv.totalCents / 100).toFixed(2),
    (inv.amountPaidCents / 100).toFixed(2),
    (inv.amountDueCents / 100).toFixed(2),
  ])
  const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `billing-report-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function ReportsTab() {
  const { t, i18n } = useTranslation('dashboard')
  const timezone = useTimezone()
  const [range, setRange] = useState<Range>('12')
  const [sorting, setSorting] = useState<any>([])

  const [invoices, setInvoices] = useState<InvoiceSummary[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setIsLoading(true)
    setError(null)
    try {
      const view = await listInvoices()
      setInvoices(view.invoices)
      setHasMore(view.hasMore)
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t('billing.invoicesLoadFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const inRange = useMemo(() => {
    const first = firstMonthOf(range, timezone)
    return first ? invoices.filter((inv) => monthKey(inv.createdAt, timezone) >= first) : invoices
  }, [invoices, range, timezone])

  const counted = useMemo(() => inRange.filter((inv) => inv.status !== 'void'), [inRange])

  const totalsByCurrency = useMemo(() => {
    const totals = new Map<string, Totals>()
    for (const inv of counted) {
      const tot = totals.get(inv.currency) ?? { billedCents: 0, paidCents: 0, outstandingCents: 0 }
      tot.billedCents += inv.totalCents
      tot.paidCents += inv.amountPaidCents
      if (inv.status === 'open') tot.outstandingCents += inv.amountDueCents
      totals.set(inv.currency, tot)
    }
    return totals
  }, [counted])

  const monthRows = useMemo(() => {
    const rows = new Map<string, MonthRow>()
    for (const inv of counted) {
      const month = monthKey(inv.createdAt, timezone)
      const key = `${month}|${inv.currency}`
      const row = rows.get(key) ?? {
        key,
        month,
        currency: inv.currency,
        invoiceCount: 0,
        billedCents: 0,
        paidCents: 0,
        outstandingCents: 0,
      }
      row.invoiceCount += 1
      row.billedCents += inv.totalCents
      row.paidCents += inv.amountPaidCents
      if (inv.status === 'open') row.outstandingCents += inv.amountDueCents
      rows.set(key, row)
    }
    return [...rows.values()].sort((a, b) => b.month.localeCompare(a.month))
  }, [counted, timezone])

  // One figure per currency, e.g. "$98.00" or "$98.00 · €20.00". An empty range
  // shows zero in the currency the organization is billed in. Same formatting
  // as the Invoices tab (no locale), so the two never disagree.
  const sumLabel = (field: keyof Totals) => {
    if (totalsByCurrency.size === 0) return formatMoney(0, invoices[0]?.currency ?? 'usd')
    return [...totalsByCurrency.entries()].map(([cur, tot]) => formatMoney(tot[field], cur)).join(' · ')
  }

  const columns: DataTableColumn<MonthRow>[] = [
    {
      id: 'month',
      header: t('billing.month'),
      accessorKey: 'month',
      cell: (row) => <span className="text-sm font-medium text-foreground">{monthLabel(row.month, i18n.language)}</span>,
    },
    {
      id: 'invoiceCount',
      header: t('billing.invoices'),
      accessorKey: 'invoiceCount',
      cell: (row) => <span className="text-sm text-muted-foreground">{row.invoiceCount}</span>,
    },
    {
      id: 'billed',
      header: t('billing.billed'),
      accessorKey: 'billedCents',
      cell: (row) => <span className="text-sm font-medium text-foreground">{formatMoney(row.billedCents, row.currency)}</span>,
    },
    {
      id: 'paid',
      header: t('billing.paid'),
      accessorKey: 'paidCents',
      cell: (row) => <span className="text-sm text-muted-foreground">{formatMoney(row.paidCents, row.currency)}</span>,
    },
    {
      id: 'outstanding',
      header: t('billing.outstanding'),
      accessorKey: 'outstandingCents',
      cell: (row) => <span className="text-sm text-muted-foreground">{formatMoney(row.outstandingCents, row.currency)}</span>,
    },
  ]

  const ready = !isLoading && !error
  const summary = [
    { label: t('billing.totalBilled'), value: sumLabel('billedCents') },
    { label: t('billing.totalPaid'), value: sumLabel('paidCents') },
    { label: t('billing.outstanding'), value: sumLabel('outstandingCents') },
    { label: t('billing.invoices'), value: String(counted.length) },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Select
          value={range}
          onChange={(v) => setRange(v as Range)}
          options={[
            { value: '3', label: t('billing.last3Months') },
            { value: '6', label: t('billing.last6Months') },
            { value: '12', label: t('billing.last12Months') },
            { value: 'all', label: t('billing.allTime') },
          ]}
        />
        <Button variant="outline" size="sm" disabled={!ready || inRange.length === 0} onClick={() => downloadCsv(inRange)}>
          <Download className="h-4 w-4 mr-2" />
          {t('billing.exportCsv')}
        </Button>
      </div>

      {error ? (
        <ErrorState
          title={t('billing.invoicesLoadFailed')}
          description={error}
          action={
            <Button variant="outline" size="sm" onClick={() => void load()}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {summary.map((card) => (
              <Card key={card.label}>
                <CardContent className="pt-6">
                  {ready ? (
                    <div className="text-2xl font-bold text-foreground">{card.value}</div>
                  ) : (
                    <div className="h-8 w-24 animate-pulse rounded bg-muted" />
                  )}
                  <p className="text-sm text-muted-foreground">{card.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <FormSection title={t('billing.monthlyBreakdown')}>
            <DataTable
              data={monthRows}
              columns={columns}
              sorting={sorting}
              onSortingChange={setSorting}
              isLoading={isLoading && invoices.length === 0}
              emptyMessage={invoices.length === 0 ? t('billing.noInvoicesYet') : t('billing.noInvoicesInRange')}
            />
            {hasMore && (
              <p className="mt-3 text-sm text-muted-foreground">
                {t('billing.reportLimitedToLatest', { count: invoices.length })}
              </p>
            )}
          </FormSection>
        </>
      )}
    </div>
  )
}
